import React from 'react';
import { Input, Checkbox, Space, Button } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';

interface FilterDropdownProps {
  confirm: (param?: { closeDropdown: boolean }) => void;
  clearFilters?: () => void;
  selectedKeys: any[];
  setSelectedKeys: (vals: any[]) => void;
  title: string;
  options: { value: any; label: string }[];
  currentSelected?: any[];
  onUpdate?: (vals: any[]) => void;
}

const SearchCheckboxFilterDropdown: React.FC<FilterDropdownProps> = ({
  confirm,
  clearFilters,
  selectedKeys,
  setSelectedKeys,
  title,
  options,
  currentSelected,
  onUpdate,
}) => {
  const [search, setSearch] = React.useState('');
  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  // Determine which selection state and update function to use
  const actualSelected =
    currentSelected !== undefined ? currentSelected : selectedKeys;
  const actualOnUpdate = (vals: any[]) => {
    if (onUpdate) {
      onUpdate(vals);
    } else {
      setSelectedKeys(vals);
    }
  };

  return (
    <div style={{ padding: 12, minWidth: 260 }}>
      <div style={{ marginBottom: 8, fontWeight: 500 }}>{title}</div>
      <Input
        placeholder={`Tìm ${title.toLowerCase()}...`}
        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 12, borderRadius: 4 }}
        allowClear
      />
      <div
        style={{
          maxHeight: 250,
          overflowY: 'auto',
          marginBottom: 12,
          border: '1px solid #f0f0f0',
          borderRadius: 4,
          padding: '8px',
          backgroundColor: '#fafafa',
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((opt) => (
            <div
              key={opt.value}
              style={{
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Checkbox
                checked={actualSelected.includes(opt.value)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const next = checked
                    ? [...actualSelected, opt.value]
                    : actualSelected.filter((v: any) => v !== opt.value);
                  actualOnUpdate(next);
                }}
              >
                <span style={{ fontSize: '14px' }}>{opt.label}</span>
              </Checkbox>
            </div>
          ))
        ) : (
          <div
            style={{
              color: '#bfbfbf',
              textAlign: 'center',
              padding: '20px 0',
            }}
          >
            Không tìm thấy kết quả
          </div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingTop: 8,
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <Space>
          <Button
            size="small"
            type="link"
            style={{ padding: 0 }}
            onClick={() => actualOnUpdate(options.map((o) => o.value))}
          >
            Tất cả
          </Button>
          <Button
            size="small"
            type="link"
            style={{ padding: 0 }}
            onClick={() => {
              actualOnUpdate([]);
              clearFilters?.();
            }}
          >
            Xóa hết
          </Button>
        </Space>
        <Button
          type="primary"
          size="small"
          onClick={() => confirm()}
          style={{ width: 70 }}
        >
          Lọc
        </Button>
      </div>
    </div>
  );
};

const SimpleCheckboxFilterDropdown: React.FC<FilterDropdownProps> = ({
  confirm,
  clearFilters,
  selectedKeys,
  setSelectedKeys,
  title,
  options,
  currentSelected,
  onUpdate,
}) => {
  const actualSelected =
    currentSelected !== undefined ? currentSelected : selectedKeys;
  const actualOnUpdate = (vals: any[]) => {
    if (onUpdate) {
      onUpdate(vals);
    } else {
      setSelectedKeys(vals);
    }
  };

  return (
    <div style={{ padding: 12, minWidth: 180 }}>
      <div style={{ marginBottom: 12, fontWeight: 500 }}>{title}</div>
      <div style={{ marginBottom: 16 }}>
        {options.map((opt) => (
          <div key={opt.value} style={{ marginBottom: 8 }}>
            <Checkbox
              checked={actualSelected.includes(opt.value)}
              onChange={(e) => {
                const checked = e.target.checked;
                const next = checked
                  ? [...actualSelected, opt.value]
                  : actualSelected.filter((v: any) => v !== opt.value);
                actualOnUpdate(next);
              }}
            >
              {opt.label}
            </Checkbox>
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingTop: 10,
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <Button
          size="small"
          type="link"
          style={{ padding: 0 }}
          onClick={() => {
            actualOnUpdate([]);
            clearFilters?.();
          }}
        >
          Xóa hết
        </Button>
        <Button
          type="primary"
          size="small"
          onClick={() => confirm()}
          style={{ width: 60 }}
        >
          Lọc
        </Button>
      </div>
    </div>
  );
};

/**
 * Filter dropdown with search and checkboxes.
 * Suitable for columns with many options (e.g., Bank, Partner).
 */
export const getSearchCheckboxFilterProps = (
  title: string,
  options: { value: any; label: string }[],
  currentSelected?: any[],
  onUpdate?: (vals: any[]) => void
) => {
  return {
    filterDropdown: (props: any) => (
      <SearchCheckboxFilterDropdown
        {...props}
        title={title}
        options={options}
        currentSelected={currentSelected}
        onUpdate={onUpdate}
      />
    ),
    filterIcon: (filtered: boolean) => (
      <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
  };
};

/**
 * Filter dropdown with only checkboxes (no search).
 * Suitable for columns with few options (e.g., Status, Activity).
 */
export const getSimpleCheckboxFilterProps = (
  title: string,
  options: { value: any; label: string }[],
  currentSelected?: any[],
  onUpdate?: (vals: any[]) => void
) => {
  return {
    filterDropdown: (props: any) => (
      <SimpleCheckboxFilterDropdown
        {...props}
        title={title}
        options={options}
        currentSelected={currentSelected}
        onUpdate={onUpdate}
      />
    ),
    filterIcon: (filtered: boolean) => (
      <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
  };
};
